/**
 *  ÓRAREND NYOMTATÁSA – ajtóra, faliújságra kiragasztható heti lap.
 *
 *  A naptárkomponenst nem nyomtatjuk közvetlenül: a FullCalendar rácsa a görgethető
 *  ion-content-ben, a 15 perces sávokkal és a szerkesztő „+" helyekkel papíron
 *  olvashatatlan és nyomtatónként máshogy törik. Helyette a megjelenített hét óráiból
 *  egy önálló, A4 fekvő táblázat készül (óra × nap), és egy láthatatlan iframe-ben
 *  nyomtatjuk ki – a képernyő állapotához nem nyúl.
 *
 *  Minden nézetben ugyanaz a lap, csak az a szempont marad le a cellákból, amelyre
 *  a nézet szűr (a teremlapon nem írjuk ki minden órához a termet, stb.).
 */

export type NyomtatasNezet = 'osztaly' | 'tanar' | 'terem' | 'tantargy';

export interface NyomtatasBeallitas {
  nezet: NyomtatasNezet;
  /** A kiválasztott osztály / tanár / terem / tantárgy azonosítója (a helyettesítések szűréséhez). */
  kivalasztott: string | null;
  cim: string;
  intezmeny?: string;
  /** Az iskola logója (abszolút cím) – a jobb felső sarokba; ha nincs vagy nem töltődik be, a név áll ott. */
  iskolaLogo?: string;
  /** A SULINFORM logója (abszolút cím) – a jobb alsó sarokba. */
  sulinformLogo?: string;
  /** A megjelenített hét hétfője. */
  hetKezdete: Date;
  datumMutat: boolean;
  /** Ki: a tartó tanár és az állandó órák; be: a hét tényleges helyettesítései is. */
  helyettesitesekkel: boolean;
  csengetes: { oraszam: number; kezdes?: string; vege?: string }[];
  orak: any[];
}

const NAPNEVEK = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat'];
const CIMKE: Record<NyomtatasNezet, string> = {
  osztaly: 'Osztály', tanar: 'Tanár', terem: 'Terem', tantargy: 'Tantárgy',
};

const esc = (v: unknown) => String(v ?? '').trim()
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Az óra napja a hét hétfőjéhez képest (0 = hétfő). A dátum ÉÉÉÉ-HH-NN része dönt, nem az időzóna. */
function napIndex(datum: unknown, hetfo: Date): number {
  const s = String(datum ?? '');
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  let d: Date;
  if (m && !s.includes('T')) d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  else { const x = new Date(s); d = new Date(x.getFullYear(), x.getMonth(), x.getDate()); }
  const h = new Date(hetfo.getFullYear(), hetfo.getMonth(), hetfo.getDate());
  return Math.round((d.getTime() - h.getTime()) / 86400000);
}

const ido = (t?: string) => (t ? String(t).slice(0, 5) : '');

function cellaTartalom(o: any, b: NyomtatasBeallitas): string {
  const sorok: string[] = [];
  sorok.push(`<div class="tg">${esc(o.tantargy) || '—'}</div>`);
  if (b.nezet !== 'osztaly') {
    const cel = String(o.osztaly || '').trim() || String(o.csoportnev || '').trim();
    if (cel) sorok.push(`<div class="cel">${esc(cel)}</div>`);
  } else if (o.csoportnev) {
    sorok.push(`<div class="cel">${esc(o.csoportnev)}</div>`);
  }
  if (b.nezet !== 'tanar') {
    if (b.helyettesitesekkel && o.helyettesito) {
      sorok.push(`<div class="tanar"><b>${esc(o.helyettesito)}</b> <span class="helyett">(helyett: ${esc(o.tarto)})</span></div>`);
    } else if (o.tarto) {
      sorok.push(`<div class="tanar">${esc(o.tarto)}</div>`);
    }
  } else if (b.helyettesitesekkel && o.helyettesito) {
    const sajat = String(o.helyettesito_tanar ?? '') === String(b.kivalasztott ?? '');
    sorok.push(`<div class="helyett">${sajat ? `helyettesítés (${esc(o.tarto)} helyett)` : `helyettesíti: ${esc(o.helyettesito)}`}</div>`);
  }
  if (b.nezet !== 'terem' && o.terem) sorok.push(`<div class="terem">Terem: ${esc(o.terem)}</div>`);
  return `<div class="ora">${sorok.join('')}</div>`;
}

export function orarendNyomtatasHtml(b: NyomtatasBeallitas): string {
  const hetfo = b.hetKezdete;

  /*  Helyettesítések nélkül az ÁLLANDÓ órarend kell: a tanárlapon a más helyett
   *  tartott óra nem az övé, a többi lapon pedig a tartó tanár neve áll. */
  const orak = (b.orak || []).filter((o) => {
    if (b.helyettesitesekkel || b.nezet !== 'tanar' || !o.helyettesito) return true;
    return String(o.tanar ?? '') === String(b.kivalasztott ?? '');
  });

  // rács: nap → óra → órák
  const racs = new Map<string, any[]>();
  let utolsoOra = 0;
  let nulladik = false;
  let szombat = false;
  for (const o of orak) {
    const nap = napIndex(o.datum, hetfo);
    if (nap < 0 || nap > 5) continue;
    const kezd = Number(o.ora);
    if (!Number.isFinite(kezd)) continue;
    const hossz = Math.max(1, Number(o.hossz) || 1);
    for (let h = kezd; h < kezd + hossz; h++) {
      const k = `${nap}|${h}`;
      if (!racs.has(k)) racs.set(k, []);
      racs.get(k)!.push(o);
      utolsoOra = Math.max(utolsoOra, h);
      if (h === 0) nulladik = true;
    }
    if (nap === 5) szombat = true;
  }

  const csengetes = new Map((b.csengetes || []).map((c) => [Number(c.oraszam), c]));
  const csengetesMax = Math.max(0, ...(b.csengetes || []).map((c) => Number(c.oraszam) || 0));
  const elsoSor = nulladik ? 0 : 1;
  const utolsoSor = Math.max(utolsoOra, csengetesMax, elsoSor);
  const napok = szombat ? 6 : 5;

  const datumok = Array.from({ length: napok }, (_, i) => {
    const d = new Date(hetfo.getFullYear(), hetfo.getMonth(), hetfo.getDate() + i);
    return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}.`;
  });
  const vasarnap = new Date(hetfo.getFullYear(), hetfo.getMonth(), hetfo.getDate() + napok - 1);
  const hetSzoveg = `${hetfo.getFullYear()}. ${hetfo.toLocaleDateString('hu-HU', { month: 'long' })} ${hetfo.getDate()}. – `
    + `${vasarnap.toLocaleDateString('hu-HU', { month: 'long' })} ${vasarnap.getDate()}.`;

  let fej = '<tr><th class="oraoszlop">Óra</th>';
  for (let i = 0; i < napok; i++) {
    fej += `<th>${NAPNEVEK[i]}${b.datumMutat ? `<span class="nap-datum">${datumok[i]}</span>` : ''}</th>`;
  }
  fej += '</tr>';

  let torzs = '';
  for (let h = elsoSor; h <= utolsoSor; h++) {
    const c = csengetes.get(h);
    torzs += `<tr><th class="oraoszlop"><span class="oraszam">${h}.</span>`
      + (c?.kezdes ? `<span class="idopont">${ido(c.kezdes)}–${ido(c.vege)}</span>` : '') + '</th>';
    for (let n = 0; n < napok; n++) {
      const cella = (racs.get(`${n}|${h}`) || [])
        .sort((a, x) => String(a.tantargy || '').localeCompare(String(x.tantargy || ''), 'hu'));
      torzs += `<td${cella.length > 1 ? ' class="tobb"' : ''}>${cella.map((o) => cellaTartalom(o, b)).join('')}</td>`;
    }
    torzs += '</tr>';
  }

  const iskolaNev = b.intezmeny ? `<div class="iskola-nev"${b.iskolaLogo ? ' hidden' : ''}>${esc(b.intezmeny)}</div>` : '';
  const iskola = b.iskolaLogo
    ? `<img class="iskola-logo" src="${esc(b.iskolaLogo)}" alt="${esc(b.intezmeny)}"
         onerror="this.remove();var n=document.querySelector('.iskola-nev');if(n)n.hidden=false">${iskolaNev}`
    : iskolaNev;

  return `<!doctype html><html lang="hu"><head><meta charset="utf-8">
<title>Órarend – ${esc(b.cim)}</title>
<style>
  @page { size: A4 landscape; margin: 9mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; color: #111;
         -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .lap { display: flex; flex-direction: column; height: 191mm; }
  header { display: flex; align-items: flex-end; justify-content: space-between; gap: 12mm;
           border-bottom: 1.2mm solid #111; padding-bottom: 2.5mm; margin-bottom: 3mm; }
  .cimke { font-size: 10pt; letter-spacing: .12em; text-transform: uppercase; color: #555; }
  h1 { margin: .5mm 0 0; font-size: 30pt; line-height: 1.05; font-weight: 700; }
  .jobb { display: flex; flex-direction: column; align-items: flex-end; gap: 1mm; text-align: right; font-size: 10pt; color: #444; }
  .iskola-logo { max-height: 20mm; max-width: 60mm; object-fit: contain; }
  .iskola-nev[hidden] { display: none; }
  table { width: 100%; flex: 1; border-collapse: collapse; table-layout: fixed; }
  th, td { border: .3mm solid #9a9a9a; }
  thead th { background: #e9e9e9; font-size: 11pt; padding: 1.5mm 1mm; font-weight: 700; }
  .nap-datum { display: block; font-weight: 400; font-size: 8.5pt; color: #555; }
  th.oraoszlop { width: 17mm; background: #f3f3f3; font-weight: 400; }
  .oraszam { display: block; font-size: 14pt; font-weight: 700; }
  .idopont { display: block; font-size: 7.5pt; color: #555; font-variant-numeric: tabular-nums; }
  td { vertical-align: middle; text-align: center; padding: .8mm 1mm; }
  .ora { line-height: 1.15; }
  .ora + .ora { border-top: .25mm dashed #aaa; margin-top: .8mm; padding-top: .8mm; }
  .tg { font-size: 11.5pt; font-weight: 700; }
  .cel { font-size: 9.5pt; font-weight: 600; }
  .tanar, .terem { font-size: 8.5pt; color: #333; }
  .helyett { font-size: 7.5pt; color: #555; font-style: italic; }
  td.tobb .tg { font-size: 9.5pt; }
  td.tobb .cel, td.tobb .tanar, td.tobb .terem { font-size: 7.5pt; }
  footer { margin-top: 2mm; font-size: 8pt; color: #666; display: flex; justify-content: space-between; align-items: center; }
  .sulinform-logo { height: 14mm; width: auto; }
</style></head><body><div class="lap">
<header>
  <div><div class="cimke">${CIMKE[b.nezet]}</div><h1>${esc(b.cim)}</h1></div>
  <div class="jobb">${iskola}${b.datumMutat ? `<div>${esc(hetSzoveg)}</div>` : ''}</div>
</header>
<table><thead>${fej}</thead><tbody>${torzs}</tbody></table>
<footer><span>Nyomtatva: ${esc(new Date().toLocaleDateString('hu-HU'))}</span>${b.sulinformLogo ? `<img class="sulinform-logo" src="${esc(b.sulinformLogo)}" alt="SULINFORM" onerror="this.remove()">` : ''}</footer>
</div></body></html>`;
}

/**
 *  Kinyomtatja a kész lapot egy láthatatlan iframe-ben. Ahol a böngésző ezt nem
 *  engedi (egyes mobil nézetek), új lapon nyitja meg, onnan nyomtatható.
 */
export function nyomtatHtml(html: string): void {
  const keret = document.createElement('iframe');
  keret.setAttribute('aria-hidden', 'true');
  keret.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(keret);
  let kesz = false;
  const takarit = () => { if (!kesz) { kesz = true; setTimeout(() => keret.remove(), 500); } };
  try {
    const ablak = keret.contentWindow!;
    const doc = ablak.document;
    doc.open(); doc.write(html); doc.close();
    /*  A Chrome print()-je a párbeszéd bezárásáig vár, a Firefoxé nem: ott az iframe
     *  korai eltávolítása a nyomtatást is elvinné. Ezért az afterprint takarít, a
     *  percenkénti tartalék csak arra az esetre, ha az esemény elmaradna. */
    ablak.addEventListener('afterprint', takarit);
    setTimeout(takarit, 60000);
    let elindult = false;
    const indit = () => {
      if (elindult) return;
      elindult = true;
      ablak.focus();
      ablak.print();
    };
    /*  A logók betöltése előtt nyomtatva üres hely maradna a helyükön: kivárjuk a
     *  képeket (a hibásat is – az a helyére a nevet teszi), de legfeljebb 3 mp-ig. */
    const kepek = Array.from(doc.images || []);
    let hatra = kepek.filter((k) => !k.complete).length;
    const egyKesz = () => { if (--hatra <= 0) setTimeout(indit, 100); };
    kepek.filter((k) => !k.complete).forEach((k) => {
      k.addEventListener('load', egyKesz);
      k.addEventListener('error', egyKesz);
    });
    if (hatra <= 0) setTimeout(indit, 250);
    setTimeout(indit, 3000);
  } catch (e) {
    keret.remove();
    const ablak = window.open('', '_blank');
    if (ablak) { ablak.document.write(html); ablak.document.close(); ablak.focus(); }
  }
}

export const __teszt = { napIndex };
